// v4 Task #10 — Closed Items archive view.
//
// Per-project tab listing every closed Item regardless of age, with
// filters (Category, date range, owner) + drill-in to the Item's full
// Action history + reopen affordance. Contrasts with the briefing's
// 2-week closed retention (Decision 4.6) — this is the long-tail view.
//
// Data source: GET /closed-items (session-authenticated; reads the
// snapshot the agent pushed via PUT /admin/closed-items/:slug on the
// last weekly run).
//
// Reopen flow: posts `work_item_reopen` feedback event keyed against
// the most-recent briefing's run_id (the closest available "anchor"
// run since the Closed Items view itself isn't bound to a run).

import { getJson, postJson } from "./api";
import { renderBreadcrumb } from "./briefings";
import type { BreadcrumbView } from "./briefings";

/**
 * #39 item 2 — the "Closed · 2026-08-04 · by Ray Lastra" badge text, shared
 * by the Item header and the Action rows so the two tiers never attribute
 * differently (they did: the agent emitted `closed_by` for Actions under an
 * OPEN parent and not for the 142 under a CLOSED one).
 *
 * Every part is optional and simply drops out — a missing date or a
 * suppressed actor narrows the badge rather than leaving a dangling
 * separator. Pure, because the Browser pane cannot verify rendered SPA
 * output; the unit test is the verification surface.
 */
export function closedBadgeText(
  verb: string, closedAt: string | null, closedBy: string | null,
): string {
  const parts = [verb];
  if (closedAt) parts.push(String(closedAt).slice(0, 10));
  if (closedBy) parts.push(`by ${closedBy}`);
  return parts.join(" · ");
}

export interface ClosedAction {
  /** #90 step 10 — move provenance, from the SAME durable record the
   * briefing reads, so this row cannot contradict the briefing. */
  breadcrumb?: BreadcrumbView;
  ai_slug: string;
  serial: number | null;
  display_number: string | null;
  text: string;
  narrative: string | null;
  priority_status: string;
  status: "open" | "closed";
  assignee: string | null;
  due: string | null;
  first_seen: string | null;
  closed_at: string | null;
  // #39 item 2: WHO closed it, already resolved agent-side to a display
  // name ("Ray Lastra" / "Agent Operator"). Never a raw email — the agent
  // suppresses rather than ship an address it cannot resolve, so `null`
  // means "render no attribution", not "unknown actor".
  closed_by: string | null;
  closed_reason: string | null;
  reason: string | null;
  impact: string | null;
  off_hold_condition: string | null;
  kil_marked: boolean;
  action_type: string;
}

export interface ClosedItem {
  /** #90 step 10 — move provenance, from the SAME durable record the
   * briefing reads, so this row cannot contradict the briefing. */
  breadcrumb?: BreadcrumbView;
  slug: string;
  serial: number | null;
  display_number: string | null;
  // #82: "closed" (the Item itself is closed) or "open" (the Item is still
  // active but carries standalone closed Actions — surfaced here so they're
  // findable, badged "Open · contains closed actions"). Absent ⇒ closed.
  status?: "open" | "closed";
  name: string;
  narrative: string | null;
  summary: string | null;
  thread_slug: string | null;
  thread_name: string | null;
  thread_display_number: string | null;
  first_seen: string | null;
  start_date: string | null;
  closed_at: string | null;
  closed_by: string | null;
  closed_reason: string | null;
  kil_marked: boolean;
  responsible_party: {
    name: string;
    email: string | null;
    source: string | null;
    locked_at: string | null;
  } | null;
  action_items: ClosedAction[];
}

// B2 (2026-06-26): acknowledged ("seen, in-project, NOT tracked") entities —
// the third terminal status. Rendered in the Closed Archive's "Acknowledged /
// Not Tracked" section, distinct from closed (work done). Carry the acknowledged
// anchor (when / by whom) instead of closed_*.
export interface AcknowledgedItem {
  slug: string;
  serial: number | null;
  display_number: string | null;
  name: string;
  narrative: string | null;
  summary: string | null;
  thread_slug: string | null;
  thread_name: string | null;
  thread_display_number: string | null;
  first_seen: string | null;
  acknowledged_at: string | null;
  acknowledged_by: string | null;
  kil_marked: boolean;
}

export interface AcknowledgedAction {
  ai_slug: string;
  serial: number | null;
  display_number: string | null;
  text: string;
  work_item_slug: string;
  work_item_name: string | null;   // parent Item context (the Item stays open)
  thread_slug: string | null;      // parent thread — for the Track-this target id
  acknowledged_at: string | null;
  acknowledged_by: string | null;
}

// Client 2026-07-24: not_relevant ("not this project") — the fourth terminal
// status. It was the only disposition with NO surface anywhere in the SPA (the
// briefing drops it silently by design, and this archive collected only closed
// + acknowledged), so a mis-click could not be undone. Its own section, with a
// Restore action. Who/when comes from the `rejected` determination rather than
// an anchor table, so it also carries the rejection reason.
export interface NotRelevantItem {
  slug: string;
  serial: number | null;
  display_number: string | null;
  name: string;
  narrative: string | null;
  summary: string | null;
  thread_slug: string | null;
  thread_name: string | null;
  thread_display_number: string | null;
  first_seen: string | null;
  rejected_at: string | null;
  rejected_by: string | null;
  rejected_reason: string | null;
  kil_marked: boolean;
}

export interface NotRelevantAction {
  ai_slug: string;
  serial: number | null;
  display_number: string | null;
  text: string;
  work_item_slug: string;
  work_item_name: string | null;   // parent Item context (the Item stays open)
  thread_slug: string | null;      // parent thread — for the Restore target id
  rejected_at: string | null;
  rejected_by: string | null;
  rejected_reason: string | null;
}

export interface ClosedItemsSnapshot {
  project_slug: string;
  snapshot_at: string | null;
  closed_items: ClosedItem[];
  // #82 (2026-06-29): OPEN Items that carry standalone closed Actions, shaped
  // like ClosedItem (status="open") so they render through the same Item card
  // construct, inline with the closed Items. Optional — the snapshot rides a
  // passthrough schema and the agent now always emits it (possibly empty).
  open_items_with_closed_actions?: ClosedItem[];
  // B2 — optional so pre-B2 snapshots still parse (the snapshot rides a
  // passthrough schema; the agent now always emits these, possibly empty).
  acknowledged_items?: AcknowledgedItem[];
  acknowledged_actions?: AcknowledgedAction[];
  // Optional for the same reason — snapshots predating 2026-07-24 lack them.
  not_relevant_items?: NotRelevantItem[];
  not_relevant_actions?: NotRelevantAction[];
}

export async function fetchClosedItems(): Promise<ClosedItemsSnapshot> {
  return getJson<ClosedItemsSnapshot>("/closed-items");
}

interface BriefingsListResponse {
  briefings: Array<{ run_id: string }>;
}

async function latestRunId(): Promise<string | null> {
  // Reopen events are POSTed under /feedback/:runId; the Closed Items
  // view has no inherent runId, so we anchor against the most-recent
  // briefing. Returns null when no briefings exist yet.
  const list = await getJson<BriefingsListResponse>("/briefings");
  return list.briefings[0]?.run_id ?? null;
}

// The absorber decodes a work-item target as `thread:<t>/work_item:<wi>` or
// `unthreaded/work_item:<wi>` and RAISES on anything else
// (feedback_absorb.py::_parse_work_item_target — "target.id missing work_item:
// prefix"). Every Item-tier call here used to post a BARE slug, so item-level
// Reopen and Track-this dead-lettered on arrival while their Action-tier twins
// — which always built the prefixed form — worked. The contract fixture
// (tests/test_absorb_contract.py T_WI) uses the prefixed form, so it validated
// the contract but never what the SPA actually sends.
export function workItemTargetId(threadSlug: string | null, slug: string): string {
  return threadSlug
    ? `thread:${threadSlug}/work_item:${slug}`
    : `unthreaded/work_item:${slug}`;
}

// True when NO archive section has anything to show. Every section's count is
// a parameter so the "nothing archived yet" early return can't silently hide a
// section that does have content.
export function archiveIsEmpty(
  archiveCount: number, ackCount: number, notRelevantCount: number,
): boolean {
  return archiveCount === 0 && ackCount === 0 && notRelevantCount === 0;
}

export async function reopenClosedItem(
  slug: string, threadSlug: string | null = null,
): Promise<void> {
  const runId = await latestRunId();
  if (!runId) {
    throw new Error("No briefing run to anchor the reopen event against.");
  }
  await postJson(`/feedback/${encodeURIComponent(runId)}`, {
    kind: "work_item_reopen",
    target: { type: "work_item", id: workItemTargetId(threadSlug, slug) },
    payload: {},
  });
}

// #82: reopen a single closed Action (under a still-open Item) back to active —
// it returns to the briefing next run. Uses the existing action_reopen kind;
// same run-anchor + action target id as trackAcknowledgedAction.
export async function reopenClosedAction(
  threadSlug: string | null, workItemSlug: string, aiSlug: string,
): Promise<void> {
  const runId = await latestRunId();
  if (!runId) throw new Error("No briefing run to anchor the reopen event against.");
  const id = threadSlug
    ? `thread:${threadSlug}/work_item:${workItemSlug}/action_item:${aiSlug}`
    : `unthreaded/work_item:${workItemSlug}/action_item:${aiSlug}`;
  await postJson(`/feedback/${encodeURIComponent(runId)}`, {
    kind: "action_reopen",
    target: { type: "action_item", id },
    payload: {},
  });
}

// B2 Track-this: bring an acknowledged Item/Action back into active tracking
// (acknowledged -> open + delete the entity_acknowledged row). Same run-anchor
// pattern as reopen.
export async function trackAcknowledgedItem(
  slug: string, threadSlug: string | null = null,
): Promise<void> {
  const runId = await latestRunId();
  if (!runId) throw new Error("No briefing run to anchor the track event against.");
  await postJson(`/feedback/${encodeURIComponent(runId)}`, {
    kind: "work_item_track",
    target: { type: "work_item", id: workItemTargetId(threadSlug, slug) },
    payload: {},
  });
}

export async function trackAcknowledgedAction(
  threadSlug: string | null, workItemSlug: string, aiSlug: string,
): Promise<void> {
  const runId = await latestRunId();
  if (!runId) throw new Error("No briefing run to anchor the track event against.");
  // thread:<t>/work_item:<wi>/action_item:<ai>, or the unthreaded form. The
  // absorber keys action identity on (project, wi, ai); thread is positional.
  const id = threadSlug
    ? `thread:${threadSlug}/work_item:${workItemSlug}/action_item:${aiSlug}`
    : `unthreaded/work_item:${workItemSlug}/action_item:${aiSlug}`;
  await postJson(`/feedback/${encodeURIComponent(runId)}`, {
    kind: "action_track",
    target: { type: "action_item", id },
    payload: {},
  });
}

// Filter state — pure data, applied client-side over the fetched snapshot.
export interface ClosedItemsFilter {
  threadSlug: string | "all";
  closedAfter: string | null;   // ISO date (inclusive)
  closedBefore: string | null;  // ISO date (inclusive)
  owner: string | "all";
}

export const DEFAULT_FILTER: ClosedItemsFilter = {
  threadSlug: "all",
  closedAfter: null,
  closedBefore: null,
  owner: "all",
};

export function applyFilter(
  items: ClosedItem[],
  filter: ClosedItemsFilter,
): ClosedItem[] {
  return items.filter((item) => {
    if (filter.threadSlug !== "all" && item.thread_slug !== filter.threadSlug) {
      return false;
    }
    if (filter.owner !== "all") {
      const name = item.responsible_party?.name ?? "";
      if (name !== filter.owner) return false;
    }
    if (filter.closedAfter && (item.closed_at ?? "") < filter.closedAfter) {
      return false;
    }
    if (filter.closedBefore && (item.closed_at ?? "") > filter.closedBefore) {
      return false;
    }
    return true;
  });
}

// Build filter option sets from the snapshot — categories + owners that
// actually appear in the data, so empty selects don't show stale options.
export interface FilterOptions {
  threads: Array<{ slug: string; name: string }>;
  owners: string[];
}

export function buildFilterOptions(items: ClosedItem[]): FilterOptions {
  const threadMap = new Map<string, string>();
  const ownerSet = new Set<string>();
  for (const item of items) {
    if (item.thread_slug && item.thread_name) {
      threadMap.set(item.thread_slug, item.thread_name);
    }
    if (item.responsible_party?.name) {
      ownerSet.add(item.responsible_party.name);
    }
  }
  const threads = Array.from(threadMap.entries())
    .map(([slug, name]) => ({ slug, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
  const owners = Array.from(ownerSet).sort();
  return { threads, owners };
}

// --- Renderer ---------------------------------------------------------------

export function renderClosedItems(
  root: HTMLElement,
  snapshot: ClosedItemsSnapshot,
  onReopened: () => void | Promise<void>,
): void {
  root.innerHTML = "";
  const main = document.createElement("main");
  main.className = "closed-items-view";

  // B2 (2026-06-23 Ray): "Closed Items" → "Closed Archive" — it holds both
  // closed (work done) AND acknowledged (not tracked) long-tail entries.
  const heading = document.createElement("h2");
  heading.textContent = "Closed Archive";
  main.appendChild(heading);

  const ackItems = snapshot.acknowledged_items ?? [];
  const ackActions = snapshot.acknowledged_actions ?? [];
  const ackCount = ackItems.length + ackActions.length;
  const nrItems = snapshot.not_relevant_items ?? [];
  const nrActions = snapshot.not_relevant_actions ?? [];
  const nrCount = nrItems.length + nrActions.length;
  // #82: open Items carrying closed Actions render in the SAME list, via the
  // same Item card construct, just tagged open. Closed Items first, then these.
  const openWithClosed = snapshot.open_items_with_closed_actions ?? [];
  const archiveItems = [...snapshot.closed_items, ...openWithClosed];
  const closedN = snapshot.closed_items.length;
  const openWithClosedN = openWithClosed.length;

  const subtitle = document.createElement("p");
  subtitle.className = "closed-items-subtitle";
  if (snapshot.snapshot_at) {
    subtitle.textContent =
      `Long-tail archive — ${closedN} closed item${closedN === 1 ? "" : "s"}` +
      (openWithClosedN ? `, ${openWithClosedN} open with closed actions` : "") +
      (ackCount ? `, ${ackCount} acknowledged (not tracked)` : "") +
      (nrCount ? `, ${nrCount} not relevant` : "") +
      `. Snapshot ${snapshot.snapshot_at}.`;
  } else {
    subtitle.textContent = "Long-tail archive — nothing archived yet.";
  }
  main.appendChild(subtitle);

  // Every archive section must be counted here. Miss one and this early
  // return hides it whenever nothing else is archived — which is exactly the
  // case the Not Relevant section exists for (a recipient who rejected the
  // wrong Item and has closed nothing yet). Kept as a pure, tested helper so
  // adding a future section without wiring it in fails a test.
  if (archiveIsEmpty(archiveItems.length, ackCount, nrCount)) {
    const empty = document.createElement("p");
    empty.className = "closed-items-empty";
    empty.textContent = "Nothing closed or acknowledged yet for this project.";
    main.appendChild(empty);
    root.appendChild(main);
    return;
  }

  // --- Archive items — closed Items + open Items carrying closed Actions,
  // one list, one Item card construct (filters apply to both).
  if (archiveItems.length > 0) {
    const options = buildFilterOptions(archiveItems);
    let filter: ClosedItemsFilter = { ...DEFAULT_FILTER };

    const filtersEl = document.createElement("div");
    filtersEl.className = "closed-items-filters";

    const categorySelect = buildSelect("Category", "all", [
      { value: "all", label: "All Categories" },
      ...options.threads.map((t) => ({ value: t.slug, label: t.name })),
    ]);
    categorySelect.select.addEventListener("change", () => {
      filter = { ...filter, threadSlug: categorySelect.select.value as ClosedItemsFilter["threadSlug"] };
      repaint();
    });

    const ownerSelect = buildSelect("Owner", "all", [
      { value: "all", label: "All Owners" },
      ...options.owners.map((o) => ({ value: o, label: o })),
    ]);
    ownerSelect.select.addEventListener("change", () => {
      filter = { ...filter, owner: ownerSelect.select.value as ClosedItemsFilter["owner"] };
      repaint();
    });

    const afterInput = buildDateInput("Closed after");
    afterInput.input.addEventListener("change", () => {
      filter = { ...filter, closedAfter: afterInput.input.value || null };
      repaint();
    });

    const beforeInput = buildDateInput("Closed before");
    beforeInput.input.addEventListener("change", () => {
      filter = { ...filter, closedBefore: beforeInput.input.value || null };
      repaint();
    });

    filtersEl.appendChild(categorySelect.wrap);
    filtersEl.appendChild(ownerSelect.wrap);
    filtersEl.appendChild(afterInput.wrap);
    filtersEl.appendChild(beforeInput.wrap);
    main.appendChild(filtersEl);

    const listEl = document.createElement("div");
    listEl.className = "closed-items-list";
    main.appendChild(listEl);

    function repaint(): void {
      const visible = applyFilter(archiveItems, filter);
      listEl.innerHTML = "";
      if (visible.length === 0) {
        const none = document.createElement("p");
        none.className = "closed-items-empty";
        none.textContent = "No archive items match the current filters.";
        listEl.appendChild(none);
        return;
      }
      for (const item of visible) {
        listEl.appendChild(renderItemCard(item, onReopened));
      }
    }
    repaint();
  }

  // --- B2: Acknowledged / Not Tracked — seen-but-untracked entities.
  if (ackCount > 0) {
    main.appendChild(renderAcknowledgedSection(ackItems, ackActions, onReopened));
  }

  // --- Not Relevant — rejected placements, restorable (client 2026-07-24).
  if (nrCount > 0) {
    main.appendChild(renderNotRelevantSection(nrItems, nrActions, onReopened));
  }

  root.appendChild(main);
}

// The "Not Relevant" section — entities the recipient rejected as not-this-
// project. Deliberately the LAST section: a rejection is the least likely
// thing to be looking for, and the section exists mainly so a mis-click is
// recoverable. Restore reuses the work_item_track / action_track kind (no new
// wire kind), but is labelled "Restore to briefing" — the recipient rejected
// this, they did not untrack it.
function renderNotRelevantSection(
  items: NotRelevantItem[],
  actions: NotRelevantAction[],
  onRestored: () => void | Promise<void>,
): HTMLElement {
  const section = document.createElement("section");
  section.className = "acknowledged-section not-relevant-section";

  const h = document.createElement("h3");
  h.className = "acknowledged-heading";
  h.textContent = "Not Relevant";
  section.appendChild(h);

  const note = document.createElement("p");
  note.className = "closed-items-subtitle";
  note.textContent =
    "Marked as not belonging to this project, so they no longer appear in the " +
    "briefing and will not resurface. Restore one if it was set aside by mistake.";
  section.appendChild(note);

  const list = document.createElement("div");
  list.className = "acknowledged-list";

  const when = (at: string | null): string =>
    at ? ` · set aside ${String(at).slice(0, 10)}` : "";
  const why = (reason: string | null): string => (reason ? ` · ${reason}` : "");

  const restoreButton = (run: () => Promise<void>): HTMLButtonElement => {
    const btn = document.createElement("button");
    btn.className = "acknowledged-track-btn";
    btn.textContent = "Restore to briefing";
    btn.addEventListener("click", async () => {
      btn.disabled = true;
      btn.textContent = "Restoring…";
      try {
        await run();
        await onRestored();
      } catch (err) {
        btn.disabled = false;
        btn.textContent = "Restore to briefing";
        alert(
          `Could not restore: ${err instanceof Error ? err.message : String(err)}`,
        );
      }
    });
    return btn;
  };

  for (const it of items) {
    const row = document.createElement("article");
    row.className = "acknowledged-row";
    const title = document.createElement("div");
    title.className = "acknowledged-row-title";
    const cat = it.thread_name ? `${it.thread_name} › ` : "";
    title.textContent = `${cat}${it.name}`;
    const meta = document.createElement("span");
    meta.className = "acknowledged-row-meta";
    meta.textContent = `Item${when(it.rejected_at)}${why(it.rejected_reason)}`;
    row.appendChild(title);
    row.appendChild(meta);
    row.appendChild(
      restoreButton(() => trackAcknowledgedItem(it.slug, it.thread_slug)),
    );
    list.appendChild(row);
  }

  for (const a of actions) {
    const row = document.createElement("article");
    row.className = "acknowledged-row";
    const title = document.createElement("div");
    title.className = "acknowledged-row-title";
    title.textContent = a.text;
    const meta = document.createElement("span");
    meta.className = "acknowledged-row-meta";
    const under = a.work_item_name ? ` · under ${a.work_item_name}` : "";
    meta.textContent =
      `Action${under}${when(a.rejected_at)}${why(a.rejected_reason)}`;
    row.appendChild(title);
    row.appendChild(meta);
    row.appendChild(
      restoreButton(
        () => trackAcknowledgedAction(a.thread_slug, a.work_item_slug, a.ai_slug),
      ),
    );
    list.appendChild(row);
  }

  section.appendChild(list);
  return section;
}

// B2: the "Acknowledged / Not Tracked" section of the Closed Archive. Read-only
// findability for now ("not tracked ≠ forgotten"); Track-this (resurface) lands
// with the work_item_track / action_track kind. Distinct heading so a recipient
// never reads an acknowledged entry as completed work.
function renderAcknowledgedSection(
  items: AcknowledgedItem[],
  actions: AcknowledgedAction[],
  onTracked: () => void | Promise<void>,
): HTMLElement {
  const section = document.createElement("section");
  section.className = "acknowledged-section";

  const h = document.createElement("h3");
  h.className = "acknowledged-heading";
  h.textContent = "Acknowledged / Not Tracked";
  section.appendChild(h);

  const note = document.createElement("p");
  note.className = "closed-items-subtitle";
  note.textContent =
    "Seen and kept on the record, but not tracked as active work. New activity " +
    "on one of these resurfaces it for you to re-track, or Track this to bring " +
    "it back now.";
  section.appendChild(note);

  const list = document.createElement("div");
  list.className = "acknowledged-list";

  const when = (at: string | null): string =>
    at ? ` · acknowledged ${String(at).slice(0, 10)}` : "";

  // B2 Track-this — reverse the acknowledge, then refresh the Archive.
  const trackButton = (run: () => Promise<void>): HTMLButtonElement => {
    const btn = document.createElement("button");
    btn.className = "acknowledged-track-btn";
    btn.textContent = "Track this";
    btn.addEventListener("click", async () => {
      btn.disabled = true;
      btn.textContent = "Tracking…";
      try {
        await run();
        await onTracked();
      } catch (err) {
        btn.disabled = false;
        btn.textContent = "Track this";
        alert(`Could not track: ${err instanceof Error ? err.message : String(err)}`);
      }
    });
    return btn;
  };

  for (const it of items) {
    const row = document.createElement("article");
    row.className = "acknowledged-row";
    const title = document.createElement("div");
    title.className = "acknowledged-row-title";
    const cat = it.thread_name ? `${it.thread_name} › ` : "";
    title.textContent = `${cat}${it.name}`;
    const meta = document.createElement("span");
    meta.className = "acknowledged-row-meta";
    meta.textContent = `Item${when(it.acknowledged_at)}`;
    row.appendChild(title);
    row.appendChild(meta);
    row.appendChild(
      trackButton(() => trackAcknowledgedItem(it.slug, it.thread_slug)),
    );
    list.appendChild(row);
  }

  for (const a of actions) {
    const row = document.createElement("article");
    row.className = "acknowledged-row";
    const title = document.createElement("div");
    title.className = "acknowledged-row-title";
    title.textContent = a.text;
    const meta = document.createElement("span");
    meta.className = "acknowledged-row-meta";
    const under = a.work_item_name ? ` · under ${a.work_item_name}` : "";
    meta.textContent = `Action${under}${when(a.acknowledged_at)}`;
    row.appendChild(title);
    row.appendChild(meta);
    row.appendChild(
      trackButton(() => trackAcknowledgedAction(a.thread_slug, a.work_item_slug, a.ai_slug)),
    );
    list.appendChild(row);
  }

  section.appendChild(list);
  return section;
}

function buildSelect(
  label: string,
  initial: string,
  options: Array<{ value: string; label: string }>,
): { wrap: HTMLElement; select: HTMLSelectElement } {
  const wrap = document.createElement("label");
  wrap.className = "closed-items-filter";
  const labelEl = document.createElement("span");
  labelEl.textContent = label;
  const select = document.createElement("select");
  for (const opt of options) {
    const o = document.createElement("option");
    o.value = opt.value;
    o.textContent = opt.label;
    if (opt.value === initial) o.selected = true;
    select.appendChild(o);
  }
  wrap.appendChild(labelEl);
  wrap.appendChild(select);
  return { wrap, select };
}

function buildDateInput(
  label: string,
): { wrap: HTMLElement; input: HTMLInputElement } {
  const wrap = document.createElement("label");
  wrap.className = "closed-items-filter";
  const labelEl = document.createElement("span");
  labelEl.textContent = label;
  const input = document.createElement("input");
  input.type = "date";
  wrap.appendChild(labelEl);
  wrap.appendChild(input);
  return { wrap, input };
}

function renderItemCard(
  item: ClosedItem,
  onReopened: () => void | Promise<void>,
): HTMLElement {
  const card = document.createElement("article");
  card.className = "closed-item-card";
  card.dataset.slug = item.slug;

  const header = document.createElement("header");
  header.className = "closed-item-header";

  const title = document.createElement("h3");
  const path = item.display_number ? `${item.display_number} ` : "";
  title.textContent = `${path}${item.name}`;
  // The number above can have CHANGED since the recipient last saw this row.
  // Without the crumb this view states a bare number the briefing contradicts
  // — the disagreement the 2026-08-10 ruling forbids.
  if (item.breadcrumb) title.appendChild(renderBreadcrumb(item.breadcrumb));
  header.appendChild(title);

  // #82: same Item card construct, status-aware badge. An open Item that only
  // carries closed Actions reads "Open · contains closed actions" rather than
  // "Closed · <date>".
  const isOpen = item.status === "open";
  const statusBadge = document.createElement("span");
  statusBadge.className = isOpen
    ? "closed-item-badge closed-item-badge-open"
    : "closed-item-badge";
  statusBadge.textContent = isOpen
    ? "Open · contains closed actions"
    : closedBadgeText("Closed", item.closed_at, item.closed_by);
  header.appendChild(statusBadge);

  if (item.kil_marked) {
    const kilBadge = document.createElement("span");
    kilBadge.className = "closed-item-kil";
    kilBadge.textContent = "PSU";
    header.appendChild(kilBadge);
  }

  // Reopen Item — top-right of the header (the usual edit position). Only for a
  // genuinely closed Item; an open Item (carrying closed Actions) is reopened
  // per-Action in the drill-in below.
  if (!isOpen) {
    const reopenBtn = document.createElement("button");
    reopenBtn.type = "button";
    reopenBtn.className = "closed-item-reopen";
    reopenBtn.textContent = "Reopen Item";
    reopenBtn.addEventListener("click", async () => {
      reopenBtn.disabled = true;
      reopenBtn.textContent = "Reopening…";
      try {
        await reopenClosedItem(item.slug, item.thread_slug);
        await onReopened();
      } catch (e) {
        reopenBtn.disabled = false;
        reopenBtn.textContent = "Reopen Item";
        alert(`Reopen failed: ${e instanceof Error ? e.message : String(e)}`);
      }
    });
    header.appendChild(reopenBtn);
  }

  card.appendChild(header);

  if (item.narrative) {
    const narrative = document.createElement("p");
    narrative.className = "closed-item-narrative";
    narrative.textContent = item.narrative;
    card.appendChild(narrative);
  } else if (item.summary) {
    const summary = document.createElement("p");
    summary.className = "closed-item-narrative";
    summary.textContent = item.summary;
    card.appendChild(summary);
  }

  const meta = document.createElement("p");
  meta.className = "closed-item-meta";
  const metaParts: string[] = [];
  if (item.thread_name) metaParts.push(`Category: ${item.thread_name}`);
  if (item.responsible_party?.name) {
    metaParts.push(`Responsible: ${item.responsible_party.name}`);
  }
  if (item.start_date) metaParts.push(`Started ${item.start_date}`);
  if (item.closed_reason) metaParts.push(`Reason: ${item.closed_reason}`);
  meta.textContent = metaParts.join(" · ");
  card.appendChild(meta);

  // Drill-in: action history toggle
  if (item.action_items.length > 0) {
    const toggle = document.createElement("button");
    toggle.type = "button";
    toggle.className = "closed-item-toggle";
    toggle.textContent = `View ${item.action_items.length} action${item.action_items.length === 1 ? "" : "s"}`;
    card.appendChild(toggle);

    const history = renderActionHistory(item, onReopened);
    history.hidden = true;
    card.appendChild(history);

    toggle.addEventListener("click", () => {
      history.hidden = !history.hidden;
      toggle.textContent = history.hidden
        ? `View ${item.action_items.length} action${item.action_items.length === 1 ? "" : "s"}`
        : `Hide actions`;
    });
  }

  return card;
}

function renderActionHistory(
  item: ClosedItem,
  onReopened: () => void | Promise<void>,
): HTMLElement {
  const list = document.createElement("ul");
  list.className = "closed-item-history";
  const parentOpen = item.status === "open";
  for (const ai of item.action_items) {
    const li = document.createElement("li");
    li.className = `closed-action closed-action-${ai.status}`;

    // Number + name first, THEN the status badge, then (top-right) the reopen
    // control — same ordering as the Item header (operator 2026-06-29).
    const head = document.createElement("div");
    head.className = "closed-action-head";

    const path = ai.display_number ? `${ai.display_number} ` : "";
    const text = document.createElement("span");
    text.className = "closed-action-text";
    text.textContent = `${path}${ai.text}`;
    if (ai.breadcrumb) text.appendChild(renderBreadcrumb(ai.breadcrumb));
    head.appendChild(text);

    const isMerged = (ai.closed_reason ?? "").startsWith("merged into");
    if (ai.status === "closed") {
      const badge = document.createElement("span");
      badge.className = isMerged
        ? "closed-action-badge closed-action-badge-merged"
        : "closed-action-badge";
      badge.textContent = closedBadgeText(
        isMerged ? "Merged" : "Closed", ai.closed_at, ai.closed_by,
      );
      head.appendChild(badge);
    } else {
      const priorityChip = document.createElement("span");
      priorityChip.className = `priority priority-${priorityClass(ai.priority_status)}`;
      priorityChip.textContent = ai.priority_status;
      head.appendChild(priorityChip);
    }

    // Reopen this Action (action_reopen kind — no Worker change). Under an OPEN
    // parent it reopens directly. Under a CLOSED parent the button is grayed and
    // a confirm popover advises that reopening also reopens the parent Item,
    // then cascades both on confirm.
    if (ai.status === "closed") {
      const reopen = document.createElement("button");
      reopen.type = "button";
      reopen.className = parentOpen
        ? "closed-action-reopen"
        : "closed-action-reopen closed-action-reopen-blocked";
      reopen.textContent = "Reopen action";
      if (parentOpen) {
        reopen.addEventListener("click", async () => {
          reopen.disabled = true;
          reopen.textContent = "Reopening…";
          try {
            await reopenClosedAction(item.thread_slug, item.slug, ai.ai_slug);
            await onReopened();
          } catch (e) {
            reopen.disabled = false;
            reopen.textContent = "Reopen action";
            alert(`Reopen failed: ${e instanceof Error ? e.message : String(e)}`);
          }
        });
      } else {
        reopen.addEventListener("click", () => {
          if (li.querySelector(".closed-action-confirm")) return;  // already open
          li.appendChild(buildReopenCascadeConfirm(item, ai, onReopened));
        });
      }
      head.appendChild(reopen);
    }

    li.appendChild(head);

    if (ai.narrative) {
      const narr = document.createElement("p");
      narr.className = "closed-action-narrative";
      narr.textContent = ai.narrative;
      li.appendChild(narr);
    }
    // Meta — the close reason rendered in the SAME "Reason: <x>" format as the
    // Item meta (operator 2026-06-29), folded into one line with the rest.
    const metaParts: string[] = [];
    if (ai.closed_reason) metaParts.push(`Reason: ${ai.closed_reason}`);
    if (ai.assignee) metaParts.push(`Responsible: ${ai.assignee}`);
    if (ai.due) metaParts.push(`Due ${ai.due}`);
    if (ai.first_seen) metaParts.push(`Started ${ai.first_seen}`);
    if (metaParts.length) {
      const meta = document.createElement("span");
      meta.className = "closed-action-meta";
      meta.textContent = metaParts.join(" · ");
      li.appendChild(meta);
    }
    list.appendChild(li);
  }
  return list;
}

// #82: confirm popover for reopening a closed Action whose parent Item is also
// closed. Advises that the parent reopens too, then cascades (reopen Item +
// Action) on confirm — so we never leave an open Action under a closed Item.
function buildReopenCascadeConfirm(
  item: ClosedItem,
  ai: ClosedAction,
  onReopened: () => void | Promise<void>,
): HTMLElement {
  const pop = document.createElement("div");
  pop.className = "closed-action-confirm";

  const msg = document.createElement("p");
  msg.className = "closed-action-confirm-msg";
  msg.textContent =
    `This action's parent item “${item.name}” is closed. Reopening this ` +
    `action will also reopen the parent item.`;
  pop.appendChild(msg);

  const btns = document.createElement("div");
  btns.className = "closed-action-confirm-btns";

  const cancel = document.createElement("button");
  cancel.type = "button";
  cancel.className = "closed-action-confirm-cancel";
  cancel.textContent = "Cancel";
  cancel.addEventListener("click", () => pop.remove());

  const ok = document.createElement("button");
  ok.type = "button";
  ok.className = "closed-action-confirm-ok";
  ok.textContent = "Reopen both";
  ok.addEventListener("click", async () => {
    ok.disabled = true;
    cancel.disabled = true;
    ok.textContent = "Reopening…";
    try {
      // Item first, then the Action — leaves a clean open Item + open Action.
      await reopenClosedItem(item.slug, item.thread_slug);
      await reopenClosedAction(item.thread_slug, item.slug, ai.ai_slug);
      await onReopened();
    } catch (e) {
      ok.disabled = false;
      cancel.disabled = false;
      ok.textContent = "Reopen both";
      alert(`Reopen failed: ${e instanceof Error ? e.message : String(e)}`);
    }
  });

  btns.appendChild(cancel);
  btns.appendChild(ok);
  pop.appendChild(btns);
  return pop;
}

function priorityClass(status: string): string {
  return status.toLowerCase().replace(/[^a-z]+/g, "-");
}
